import { useConfig } from '../platform/queries.js';
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

/**
 * Grundbausteine der Oberfläche. Tippziele mindestens 44 px (KaBa-Konvention), sichtbarer Fokus, Dunkelmodus.
 * Erst bei echtem Bedarf mehrerer Apps in ein eigenes Paket `packages/ui` verschieben (ADR 0002).
 */
const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; busy?: boolean };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'secondary', busy, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-on-accent hover:bg-accent-hover',
        variant === 'secondary' && 'border border-zinc-300 bg-white hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800',
        variant === 'ghost' && 'hover:bg-zinc-100 dark:hover:bg-zinc-800',
        variant === 'danger' && 'text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cx(
        'min-h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900',
        className,
      )}
      {...rest}
    />
  );
});

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx('min-h-11 rounded-lg border border-zinc-300 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900', className)} {...rest} />;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? <span className="block text-sm text-red-700 dark:text-red-400">{error}</span> : hint ? <span className="block text-xs text-zinc-500">{hint}</span> : null}
    </label>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900', className)}>{children}</div>;
}

export function Notice({ kind = 'info', children, ...data }: { kind?: 'info' | 'error' | 'success'; children: ReactNode } & { [k: `data-${string}`]: string }) {
  return (
    <div
      {...data}
      role={kind === 'error' ? 'alert' : 'status'}
      className={cx(
        'rounded-lg px-3 py-2 text-sm',
        kind === 'info' && 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
        kind === 'error' && 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300',
        kind === 'success' && 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
      )}
    >
      {children}
    </div>
  );
}

/** Zentrierte Seite für Anmeldung, Einrichtung, Einladung */
export function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  const config = useConfig();
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <img src={config.data?.logo ?? "/icon.svg"} alt="" className="size-10 object-contain" />
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>
      <Card>{children}</Card>
    </main>
  );
}
